import React, { useEffect, useRef, useState } from 'react'
import { Button, Flex, P, LocationIcon, H2 } from '../../lib/components'
import { BACK_TO_PROJECT, CONTACT_YOUR_RM } from '../../lib/utils/message'
import { OpportunityDetailTopProps } from '../../lib/types'
import { useBreakpoint } from '../../lib/utils/hooks'
import { LG } from '../../lib/utils/constants'
import { getTextValue, getOpportunityLogoUrl } from './cms-components/utils'
import { displayTicketSize } from '../../lib/utils/helpers'
import { BriefcaseIcon } from '../../lib/components/Icon/icon-component'
import { useOpportunitySectionNavigation } from './opportunity-section-navigation-context'
import { OPPORTUNITY_SECTION_IDS } from './opportunity-detail.utils'

const OpportunityDetailTop = ({
  project,
  opportunityDetails,
  investHandleClick,
  indicateInterestHandleClick,
  isIndicatingInterest,
  backToProjectHandler,
  investButtonProperty,
  kycStatus,
}: OpportunityDetailTopProps) => {
  const { breakpoint } = useBreakpoint()
  const { setNavVisibilityAnchor } = useOpportunitySectionNavigation()
  const inlineActionsRef = useRef<HTMLDivElement>(null)
  const stickyActionsRef = useRef<HTMLDivElement>(null)
  const [showStickyActions, setShowStickyActions] = useState(false)
  const companyMetadata = (project?.metadata?.company ?? {}) as Record<string, unknown>
  const displayName = project?.companyName || project?.projectName || ''
  const foundedYear = getTextValue(
    companyMetadata.foundedYear,
    companyMetadata.founded_year,
    companyMetadata.yearFounded,
    companyMetadata.year_founded,
    companyMetadata.founded,
  )
  const companyLinks = opportunityDetails?.aboutLinks ?? []
  const logoUrl = getOpportunityLogoUrl(opportunityDetails?.logo)
  const minimumInvestmentAmount = displayTicketSize(project?.ticketSize, project?.currencyCode)
  const investorCount = Number(project?.noOfInvestors)
  const backingText =
    opportunityDetails?.showInvestorBacking === true &&
    Number.isFinite(investorCount) &&
    investorCount > 0
      ? `Backed by ${investorCount} ${investorCount === 1 ? 'client' : 'clients'}`
      : opportunityDetails?.showInvestorBacking === false
      ? opportunityDetails?.opportunityBacking?.trim() || null
      : null

  const isInvestDisabled = Boolean(
    investButtonProperty &&
      investButtonProperty.isDisable &&
      !(
        kycStatus === 'APPROVED' && investButtonProperty.buttonText === 'Complete Client Agreement'
      ),
  )

  const handleInvestClick = () => {
    if (
      investButtonProperty &&
      (!investButtonProperty.isDisable ||
        (kycStatus === 'APPROVED' &&
          investButtonProperty.buttonText === 'Complete Client Agreement'))
    ) {
      investHandleClick(project.projectSlug, project.id)
    }
  }

  const renderHeroMetaLine = (extraClassName = '') => {
    const hasMeta =
      project?.location || foundedYear || project?.assetClass || companyLinks.length > 0

    if (!hasMeta) return null

    return (
      <div className={`opportunity-hero-meta-line ${extraClassName}`.trim()}>
        {project?.location && (
          <span className='opportunity-hero-meta-item opportunity-hero-meta-item-with-icon'>
            <LocationIcon />
            <span>{project.location}</span>
          </span>
        )}
        {foundedYear && <span className='opportunity-hero-meta-item'>Founded {foundedYear}</span>}
        {project?.assetClass && (
          <span className='opportunity-hero-meta-item opportunity-hero-meta-item-with-icon'>
            <BriefcaseIcon />
            <span>{project.assetClass}</span>
          </span>
        )}
        {companyLinks.map((link) => (
          <a
            key={link.linkTitle}
            className='opportunity-hero-meta-item opportunity-hero-meta-item-link'
            href={link.linkUrl}
            rel='noreferrer'
            target='_blank'
          >
            {link.linkTitle}
          </a>
        ))}
      </div>
    )
  }

  const hasInvestmentInfoMessage = Boolean(investButtonProperty?.investmentInfo?.props?.children)

  const renderInvestButton = (testId: string) => (
    <Button
      classes={[
        breakpoint && breakpoint < LG ? '' : 'auto-btn-width',
        'opportunity-hero-invest-btn',
      ]}
      isDisabled={isInvestDisabled}
      text={investButtonProperty && investButtonProperty.buttonText}
      testId={testId}
      variant='primary-btn'
      iconVariant='right'
      IconComponent={<span className='opportunity-hero-button-arrow'>{'>'}</span>}
      handleClick={handleInvestClick}
    />
  )

  const renderLearnMoreButton = (testId: string) => (
    <Button
      classes={[
        breakpoint && breakpoint < LG ? '' : 'auto-btn-width',
        'opportunity-hero-learn-more-btn',
      ]}
      isDisabled={isIndicatingInterest}
      text={CONTACT_YOUR_RM}
      testId={testId}
      variant='secondary-btn'
      iconVariant='right'
      IconComponent={<span className='opportunity-hero-button-arrow'>{'>'}</span>}
      handleClick={indicateInterestHandleClick}
    />
  )

  const renderActionButtons = (
    investTestId: string,
    learnMoreTestId: string,
    layoutClassName = 'opportunity-hero-actions',
  ) => (
    <div className={layoutClassName}>
      {renderInvestButton(investTestId)}
      {renderLearnMoreButton(learnMoreTestId)}
    </div>
  )

  useEffect(() => {
    const node = inlineActionsRef.current
    if (!node) return undefined

    setNavVisibilityAnchor(node)

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyActions(!entry.isIntersecting)
      },
      { threshold: 0, rootMargin: '0px 0px -8px 0px' },
    )

    observer.observe(node)
    return () => {
      observer.disconnect()
      setNavVisibilityAnchor(null)
    }
  }, [setNavVisibilityAnchor])

  useEffect(() => {
    const bodyClass = 'opportunity-detail-sticky-cta-active'
    if (showStickyActions) {
      document.body.classList.add(bodyClass)
    } else {
      document.body.classList.remove(bodyClass)
    }

    return () => document.body.classList.remove(bodyClass)
  }, [showStickyActions])

  useEffect(() => {
    const node = stickyActionsRef.current
    if (!node || !showStickyActions) {
      document.documentElement.style.removeProperty('--opportunity-sticky-cta-height')
      return undefined
    }

    const updateStickyHeight = () => {
      document.documentElement.style.setProperty(
        '--opportunity-sticky-cta-height',
        `${Math.ceil(node.getBoundingClientRect().height)}px`,
      )
    }

    updateStickyHeight()

    const observer = new ResizeObserver(updateStickyHeight)
    observer.observe(node)

    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty('--opportunity-sticky-cta-height')
    }
  }, [showStickyActions])

  return (
    <section
      id={OPPORTUNITY_SECTION_IDS.overview}
      className='opportunity-overview-panel opportunity-hero-card'
    >
      <div className='opportunity-hero-breadcrumb'>
        <button
          type='button'
          className='opportunity-hero-breadcrumb-link'
          onClick={backToProjectHandler}
        >
          {BACK_TO_PROJECT}
        </button>
        <span className='opportunity-hero-breadcrumb-separator'>{'>'}</span>
        <span className='opportunity-hero-breadcrumb-current'>{displayName}</span>
      </div>

      <div className='opportunity-hero-content-layout'>
        <div data-testid='opportunity-details-top-section-info' className='opportunity-hero-copy'>
          <div className='opportunity-hero-title-row'>
            <div
              className={`opportunity-hero-logo${logoUrl ? ' opportunity-hero-logo--image' : ''}`}
              data-testid='opportunity-details-top-section-media'
            >
              {logoUrl ? (
                <img src={logoUrl} alt={`${displayName || 'Opportunity'} logo`} />
              ) : (
                displayName.charAt(0).toUpperCase() || 'O'
              )}
            </div>
            <div className='opportunity-hero-heading-block'>
              <div className='opportunity-hero-heading'>
                <H2 classes={['opportunity-details-heading']} text={displayName} />
              </div>
              {backingText && <P classes={['mt-8']} variant='default-blue' text={backingText} />}
            </div>
          </div>

          {renderHeroMetaLine()}
          <div className='opportunity-hero-summary'>
            <P
              classes={['mt-12', 'opportunity-hero-summary-copy']}
              text={project?.projectSummary}
              textSize='regular'
              variant='default-gray'
            />
          </div>
          <div ref={inlineActionsRef}>
            {renderActionButtons(
              'opp-detail-top-section-invest-btn',
              'opp-detail-top-section-learn-more-btn',
            )}
          </div>

          <Flex classes={['mt-12']}>
            {hasInvestmentInfoMessage && investButtonProperty && (
              <div className='kyc-sub-heading' data-testid='opp-detail-top-section-invest-info'>
                <p>{investButtonProperty.investmentInfo}</p>
              </div>
            )}
          </Flex>
        </div>
      </div>

      <div
        className={`opportunity-hero-sticky-actions${
          showStickyActions ? ' opportunity-hero-sticky-actions--visible' : ''
        }`}
        ref={stickyActionsRef}
        aria-hidden={!showStickyActions}
        data-testid='opportunity-hero-sticky-actions'
      >
        <div className='opportunity-hero-sticky-actions__gutter'>
          <div className='opportunity-hero-sticky-actions__main'>
            <div className='opportunity-hero-sticky-actions__track'>
              <div className='opportunity-hero-sticky-actions__inner'>
                <div className='opportunity-hero-sticky-actions__info'>
                  <p className='opportunity-hero-sticky-actions__title' title={displayName}>
                    {displayName}
                  </p>
                  <p
                    className='opportunity-hero-sticky-actions__minimum-investment'
                    data-testid='opportunity-hero-sticky-minimum-investment'
                  >
                    <span className='opportunity-hero-sticky-actions__minimum-label'>
                      Minimum Amount
                    </span>
                    <span className='opportunity-hero-sticky-actions__minimum-value'>
                      {minimumInvestmentAmount}
                    </span>
                  </p>
                  {renderHeroMetaLine('opportunity-hero-sticky-meta-line')}
                </div>
                {renderActionButtons(
                  'opp-detail-sticky-invest-btn',
                  'opp-detail-sticky-learn-more-btn',
                  'opportunity-hero-sticky-actions__buttons',
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export { OpportunityDetailTop }
